import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@resume-studio/ui";
import {
  Activity,
  BarChart3,
  CircleDot,
  Gauge,
  Megaphone,
  PlusCircle,
  ScrollText,
  User as UserIcon,
  Users,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { useAdminCampaigns } from "../../services/campaigns";
import { useAdminUsers } from "../../services/users";

interface AdminCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AdminCommandPalette = ({ open, onOpenChange }: AdminCommandPaletteProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");

  // Only hit the search endpoint once the user has typed something. The
  // empty-string case still returns recent users — that's the value of the
  // palette as a launcher, not just a navigator.
  const { data: users } = useAdminUsers(query);
  const { data: campaigns } = useAdminCampaigns();

  // Reset typed query whenever the palette closes so opening it again
  // starts from the recent / nav state instead of last query.
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const activeCampaigns = (campaigns ?? [])
    .filter((c) => c.status === "active" || c.status === "scheduled")
    .slice(0, 6);

  const userMatches = (users ?? []).slice(0, 6);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search users, campaigns, or jump to a page…"
      />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>

        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/")}>
            <Gauge className="mr-2 size-3.5 text-subtle" /> Overview
            <CommandShortcut>G O</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/campaigns")}>
            <Megaphone className="mr-2 size-3.5 text-subtle" /> Campaigns
            <CommandShortcut>G C</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/users")}>
            <Users className="mr-2 size-3.5 text-subtle" /> Users
            <CommandShortcut>G U</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/revenue")}>
            <BarChart3 className="mr-2 size-3.5 text-subtle" /> Revenue
            <CommandShortcut>G R</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/analytics")}>
            <Activity className="mr-2 size-3.5 text-subtle" /> Analytics
            <CommandShortcut>G A</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/audit")}>
            <ScrollText className="mr-2 size-3.5 text-subtle" /> Audit log
          </CommandItem>
        </CommandGroup>

        {userMatches.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={query ? "Users" : "Recent users"}>
              {userMatches.map((user) => (
                <CommandItem
                  key={user.id}
                  // `value` controls the cmdk fuzzy match. Combine name + email
                  // so typing either field surfaces the row.
                  value={`${user.name} ${user.email} ${user.username}`}
                  onSelect={() => go(`/users/${user.id}`)}
                >
                  <UserIcon className="mr-2 size-3.5 text-subtle" />
                  <span className="flex-1 truncate">{user.name || user.email}</span>
                  <span className="ml-3 truncate text-[11px] text-muted-foreground">
                    {user.email}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {activeCampaigns.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Active campaigns">
              {activeCampaigns.map((campaign) => (
                <CommandItem
                  key={campaign.id}
                  value={`${campaign.name} ${campaign.slug}`}
                  onSelect={() => go(`/campaigns/${campaign.id}`)}
                >
                  <CircleDot className="mr-2 size-3.5 text-success" />
                  <span className="flex-1 truncate">{campaign.name}</span>
                  <span className="ml-3 mono truncate text-[11px] text-muted-foreground">
                    {campaign.slug}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem onSelect={() => go("/campaigns/new")}>
            <PlusCircle className="mr-2 size-3.5 text-subtle" /> New campaign
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

/**
 * `cmd+k` (or `ctrl+k` on non-mac) toggles the palette globally inside the
 * admin shell. Lives on its own hook so we can reuse it from any future
 * trigger surface (e.g. the topbar search box).
 */
export const useAdminCommandPalette = () => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return { open, setOpen };
};
